import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  LinearProgress,
  Fade,
  Tooltip,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import axios from 'axios';
import { useThemeMode } from '../context/ThemeModeContext';

/** Purpose: validate a reset token and save a replacement password. Why: account recovery must be possible without an authenticated session. */
export default function ResetPassword() {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [step, setStep] = useState('request'); // 'request', 'verify'
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [devOTP, setDevOTP] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  const { mode, toggleMode } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();

  const isDark = mode === 'dark';

  useEffect(() => {
    const emailFromState = location.state?.email;
    if (emailFromState) {
      setEmail(emailFromState);
    }
  }, [location]);

  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setInterval(() => {
        setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Password strength checker
  /** Purpose: Provide the get password strength operation for the reset password module. Why: Callers depend on this shared result to keep the surrounding flow consistent. */
  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '', color: 'inherit' };

    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z\d]/.test(password)) strength++;

    const levels = [
      { label: 'Very Weak', color: 'error' },
      { label: 'Weak', color: 'error' },
      { label: 'Fair', color: 'warning' },
      { label: 'Good', color: 'info' },
      { label: 'Strong', color: 'success' },
      { label: 'Very Strong', color: 'success' },
    ];

    return levels[Math.min(strength, 5)];
  };

  const passwordStrength = getPasswordStrength(newPassword);

  /** Purpose: Handle handle request otp in the reset password module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleRequestOTP = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await axios.post('/api/v1/auth/forgot-password', { email });
      setSuccessMessage('Password reset verification code has been sent to your email.');
      if (response.data.devOTP) {
        setDevOTP(response.data.devOTP);
      }
      setResendCooldown(60);
      setStep('verify');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send reset code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /** Purpose: Handle handle verify and reset in the reset password module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleVerifyAndReset = async (e) => {
    e.preventDefault();

    if (!otp || !newPassword || !confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      await axios.post('/api/v1/auth/reset-password', {
        email,
        otp,
        newPassword,
      });

      setSuccessMessage('Password reset successfully! Redirecting to login...');
      setTimeout(() => {
        navigate('/login', {
          state: { message: 'Password reset successful. Please sign in with your new password.' },
        });
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password. Please check the OTP and try again.');
    } finally {
      setLoading(false);
    }
  };

  /** Purpose: Handle handle resend otp in the reset password module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleResendOTP = async () => {
    if (resendCooldown > 0 || !email) return;

    setLoading(true);
    setError('');
    try {
      const response = await axios.post('/api/v1/auth/forgot-password', { email });
      setSuccessMessage('A new verification code has been sent to your email.');
      if (response.data.devOTP) {
        setDevOTP(response.data.devOTP);
      }
      setResendCooldown(60);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resend verification code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, sm: 3 },
        background: isDark
          ? 'linear-gradient(135deg, #020617 0%, #0B0F19 50%, #0F172A 100%)'
          : 'linear-gradient(135deg, #F1F5F9 0%, #E2E8F0 50%, #EEF2F6 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Ambient background blur elements */}
      <Box
        sx={{
          position: 'absolute',
          top: -100,
          left: -100,
          width: 380,
          height: 380,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(37,99,235,0.2) 0%, transparent 70%)',
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -100,
          right: -100,
          width: 420,
          height: 420,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(147,51,234,0.18) 0%, transparent 70%)',
          filter: 'blur(70px)',
          pointerEvents: 'none',
        }}
      />

      {/* Top Controls: Dark/Light Mode toggle */}
      <Box sx={{ position: 'absolute', top: 20, right: 24, zIndex: 10 }}>
        <Tooltip title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
          <IconButton
            onClick={toggleMode}
            sx={{
              p: 1.25,
              borderRadius: '12px',
              color: isDark ? '#E2E8F0' : '#475569',
              background: isDark ? 'rgba(30, 41, 59, 0.7)' : 'rgba(255, 255, 255, 0.85)',
              border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
              backdropFilter: 'blur(8px)',
              '&:hover': {
                background: isDark ? 'rgba(51, 65, 85, 0.8)' : 'rgba(241, 245, 249, 1)',
              },
            }}
          >
            {isDark ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Main Card */}
      <Fade in timeout={400}>
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            maxWidth: 480,
            p: { xs: 3.5, sm: 4.5 },
            borderRadius: '24px',
            background: isDark ? 'rgba(15, 23, 42, 0.85)' : '#ffffff',
            backdropFilter: 'blur(20px)',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.06)',
            boxShadow: isDark
              ? '0 25px 50px -12px rgba(0, 0, 0, 0.65)'
              : '0 25px 50px -12px rgba(15, 23, 42, 0.1)',
            zIndex: 1,
          }}
        >
          {/* Brand Logo & Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #2563EB 0%, #7C3AED 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 16px -4px rgba(37, 99, 235, 0.4)',
              }}
            >
              <AutoAwesomeRoundedIcon sx={{ color: '#ffffff', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                Zelorich AI
              </Typography>
              <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B', fontWeight: 600 }}>
                Enterprise Platform
              </Typography>
            </Box>
          </Box>

          {/* Title & Description */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.75, color: isDark ? '#F8FAFC' : '#0F172A' }}>
              {step === 'request' ? 'Forgot Password' : 'Reset Password'}
            </Typography>
            <Typography variant="body2" sx={{ color: isDark ? '#94A3B8' : '#64748B', lineHeight: 1.5 }}>
              {step === 'request'
                ? "Enter your registered email address. We'll send you a 6-digit verification code to reset your password."
                : `Enter the 6-digit verification code sent to ${email} and choose a new password.`}
            </Typography>
          </Box>

          {/* Feedback Alerts */}
          {error && (
            <Alert severity="error" sx={{ mb: 2.5, borderRadius: '12px' }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success" sx={{ mb: 2.5, borderRadius: '12px' }} onClose={() => setSuccessMessage('')}>
              {successMessage}
            </Alert>
          )}

          {devOTP && (
            <Alert severity="info" sx={{ mb: 2.5, borderRadius: '12px' }}>
              Local Dev Code: <strong>{devOTP}</strong> (SMTP console mode)
            </Alert>
          )}

          {/* Step 1: Request Code Form */}
          {step === 'request' && (
            <form onSubmit={handleRequestOTP}>
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: isDark ? '#CBD5E1' : '#334155',
                    fontSize: '0.82rem',
                    display: 'block',
                    mb: 0.75,
                  }}
                >
                  Work Email
                </Typography>
                <TextField
                  fullWidth
                  type="email"
                  placeholder="name@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  autoComplete="email"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MailOutlineRoundedIcon sx={{ color: isDark ? '#64748B' : '#94A3B8', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      background: isDark ? 'rgba(30, 41, 59, 0.45)' : '#F8FAFC',
                      color: isDark ? '#FFFFFF' : '#0F172A',
                      '& fieldset': {
                        borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#E2E8F0',
                      },
                      '&:hover fieldset': {
                        borderColor: isDark ? '#3B82F6' : '#2563EB',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#2563EB',
                        borderWidth: '2px',
                      },
                    },
                  }}
                />
              </Box>

              <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={loading || !email}
                endIcon={!loading && <ArrowForwardRoundedIcon />}
                sx={{
                  py: 1.35,
                  borderRadius: '12px',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                  boxShadow: '0 4px 14px 0 rgba(37, 99, 235, 0.35)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)',
                    boxShadow: '0 6px 20px 0 rgba(37, 99, 235, 0.45)',
                  },
                }}
              >
                {loading ? <CircularProgress size={24} sx={{ color: '#ffffff' }} /> : 'Send Verification Code'}
              </Button>

              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button
                  variant="text"
                  startIcon={<ArrowBackRoundedIcon fontSize="small" />}
                  onClick={() => navigate('/login')}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    color: isDark ? '#94A3B8' : '#64748B',
                    '&:hover': { color: '#2563EB', background: 'transparent' },
                  }}
                >
                  Back to Sign In
                </Button>
              </Box>
            </form>
          )}

          {/* Step 2: Verify Code and Reset Password Form */}
          {step === 'verify' && (
            <form onSubmit={handleVerifyAndReset}>
              {/* Email summary with change option */}
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
                  Target: <strong>{email}</strong>
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setStep('request')}
                  sx={{
                    p: 0,
                    minWidth: 'auto',
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    color: '#2563EB',
                    fontWeight: 600,
                    '&:hover': { background: 'transparent', textDecoration: 'underline' },
                  }}
                >
                  Change email
                </Button>
              </Box>

              {/* 6-Digit OTP field */}
              <Box sx={{ mb: 2.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: isDark ? '#CBD5E1' : '#334155',
                    fontSize: '0.82rem',
                    display: 'block',
                    mb: 0.75,
                  }}
                >
                  6-Digit Verification Code (OTP)
                </Typography>
                <TextField
                  fullWidth
                  placeholder="123456"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  autoFocus
                  inputProps={{
                    maxLength: 6,
                    pattern: '[0-9]*',
                    style: { letterSpacing: '4px', fontWeight: 700, fontSize: '1.1rem' },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ShieldOutlinedIcon sx={{ color: isDark ? '#64748B' : '#94A3B8', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      background: isDark ? 'rgba(30, 41, 59, 0.45)' : '#F8FAFC',
                      color: isDark ? '#FFFFFF' : '#0F172A',
                      '& fieldset': {
                        borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#E2E8F0',
                      },
                      '&:hover fieldset': {
                        borderColor: isDark ? '#3B82F6' : '#2563EB',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#2563EB',
                        borderWidth: '2px',
                      },
                    },
                  }}
                />
              </Box>

              {/* New Password field */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: isDark ? '#CBD5E1' : '#334155',
                    fontSize: '0.82rem',
                    display: 'block',
                    mb: 0.75,
                  }}
                >
                  New Password
                </Typography>
                <TextField
                  fullWidth
                  type={showNewPassword ? 'text' : 'password'}
                  placeholder="Minimum 6 characters"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlinedIcon sx={{ color: isDark ? '#64748B' : '#94A3B8', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle new password visibility"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          edge="end"
                          size="small"
                          sx={{ color: isDark ? '#94A3B8' : '#64748B' }}
                        >
                          {showNewPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      background: isDark ? 'rgba(30, 41, 59, 0.45)' : '#F8FAFC',
                      color: isDark ? '#FFFFFF' : '#0F172A',
                      '& fieldset': {
                        borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#E2E8F0',
                      },
                      '&:hover fieldset': {
                        borderColor: isDark ? '#3B82F6' : '#2563EB',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#2563EB',
                        borderWidth: '2px',
                      },
                    },
                  }}
                />

                {/* Password strength meter */}
                {newPassword && (
                  <Box sx={{ mt: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
                        Password strength
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color:
                            passwordStrength.color === 'error'
                              ? '#ef4444'
                              : passwordStrength.color === 'warning'
                              ? '#f59e0b'
                              : passwordStrength.color === 'info'
                              ? '#3b82f6'
                              : '#10b981',
                        }}
                      >
                        {passwordStrength.label}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(passwordStrength.strength / 5) * 100}
                      color={passwordStrength.color}
                      sx={{ height: 6, borderRadius: 1 }}
                    />
                  </Box>
                )}
              </Box>

              {/* Confirm Password field */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: isDark ? '#CBD5E1' : '#334155',
                    fontSize: '0.82rem',
                    display: 'block',
                    mb: 0.75,
                  }}
                >
                  Confirm New Password
                </Typography>
                <TextField
                  fullWidth
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Re-enter new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  error={Boolean(confirmPassword && newPassword !== confirmPassword)}
                  helperText={confirmPassword && newPassword !== confirmPassword ? 'Passwords do not match' : ''}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlinedIcon sx={{ color: isDark ? '#64748B' : '#94A3B8', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle confirm password visibility"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          size="small"
                          sx={{ color: isDark ? '#94A3B8' : '#64748B' }}
                        >
                          {showConfirmPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      background: isDark ? 'rgba(30, 41, 59, 0.45)' : '#F8FAFC',
                      color: isDark ? '#FFFFFF' : '#0F172A',
                      '& fieldset': {
                        borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#E2E8F0',
                      },
                      '&:hover fieldset': {
                        borderColor: isDark ? '#3B82F6' : '#2563EB',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#2563EB',
                        borderWidth: '2px',
                      },
                    },
                  }}
                />
              </Box>

              <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={
                  loading ||
                  !otp ||
                  otp.length !== 6 ||
                  !newPassword ||
                  newPassword.length < 6 ||
                  !confirmPassword ||
                  newPassword !== confirmPassword
                }
                sx={{
                  py: 1.35,
                  borderRadius: '12px',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                  boxShadow: '0 4px 14px 0 rgba(37, 99, 235, 0.35)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)',
                    boxShadow: '0 6px 20px 0 rgba(37, 99, 235, 0.45)',
                  },
                }}
              >
                {loading ? <CircularProgress size={24} sx={{ color: '#ffffff' }} /> : 'Set New Password'}
              </Button>

              {/* Resend Code & Back link */}
              <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button
                  variant="text"
                  disabled={loading || resendCooldown > 0}
                  onClick={handleResendOTP}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.82rem',
                    color: '#2563EB',
                    '&:hover': { background: 'transparent', textDecoration: 'underline' },
                  }}
                >
                  {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : "Didn't receive code? Resend"}
                </Button>

                <Button
                  variant="text"
                  startIcon={<ArrowBackRoundedIcon fontSize="small" />}
                  onClick={() => navigate('/login')}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.82rem',
                    color: isDark ? '#94A3B8' : '#64748B',
                    '&:hover': { color: '#2563EB', background: 'transparent' },
                  }}
                >
                  Back to Sign In
                </Button>
              </Box>
            </form>
          )}
        </Paper>
      </Fade>

      {/* Footer Branding */}
      <Box sx={{ mt: 3, textAlign: 'center', zIndex: 1 }}>
        <Typography variant="caption" sx={{ color: isDark ? '#64748B' : '#94A3B8' }}>
          © {new Date().getFullYear()} Zelorich AI. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
}
